import { makeAutoObservable, runInAction } from 'mobx'
import { api } from 'store/api'
import { me } from 'store/me'
import { opt } from 'store/opt'
import ProfileStore from 'store/profile/ProfileStore'
import { Identity } from 'type/Identity'
import { IdentityConfig } from 'type/IdentityConfig'
import { load, save } from 'util/storage'

function key() {
  if (opt.tsum) return 'identity_id'
  return `identity_id.${opt.company}`
}

function loadIdentityId(): string | undefined {
  return load(key())
}

function saveIdentityId(id: string | undefined): void {
  save(key(), id)
}

export class IdentitiesStore {
  active_identity_id?: string

  private _config!: IdentityConfig
  private _profile!: ProfileStore
  private _profiles: ProfileStore[] = []
  private _open = false

  constructor() {
    makeAutoObservable(this)
  }

  get open(): boolean {
    return this._open
  }

  set open(value: boolean) {
    this._open = value
  }

  get profile(): ProfileStore {
    return this._profile
  }

  private set profile(profile: ProfileStore) {
    this._profile = profile
    if (profile.identity_id) {
      this.active_identity_id = profile.identity_id
      saveIdentityId(profile.identity_id)
    }
  }

  get profiles(): ProfileStore[] {
    return this._profiles
  }

  private set profiles(value: ProfileStore[]) {
    this._profiles = value
  }

  init(config: IdentityConfig, identities: Identity[]) {
    const profiles = identities.map(it => new ProfileStore(config, it))
    if (opt.basic) for (const profile of profiles) profile.basic.done = true
    this._config = config
    this.profiles = opt.single ? profiles.slice(0, 1) : profiles
    this.active_identity_id = loadIdentityId()
    this.reactivate()
  }

  async update() {
    await me.ensure
    const user_id = me.user_id
    const identities = await api.getIdentities({ user_id })
    const added = identities.find(it => !this.profiles.some(profile => profile.identity_id === it.identity_id))
    runInAction(() => {
      for (const identity of identities) {
        const profile = this.profiles.find(it => it.identity_id === identity.identity_id)
        if (profile) profile.applyIdentity(identity)
      }
      if (added) {
        if (this.profile.identity_id) this.profiles.push(new ProfileStore(this._config, added))
        else this.profile.applyIdentity(added)
      }
    })
  }

  reset() {
    for (const profile of this.profiles) {
      if (!profile.saving) profile.reset()
    }
    const { identity_id, saving } = this.profile ?? {}
    if (!identity_id && !saving) this.reactivate()
  }

  selectProfile(profile: ProfileStore | undefined) {
    if (!profile) {
      profile = this.profiles.find(it => !it.identity_id && !it.saving)
    }

    if (!profile) {
      const limit = this.profiles.length > 4
      if (limit) {
        profile = this.profiles[0]!
      } else {
        profile = new ProfileStore(this._config, undefined)
        this.profiles.push(profile)
      }
    }

    this.profile = profile
  }

  activateProfile(profile: ProfileStore) {
    if (this.profile === profile) return
    if (this.profiles.includes(profile)) {
      this.profile = profile
    }
  }

  async deleteProfile(profile: ProfileStore) {
    const { identity_id } = profile
    await profile.saving
    const index = this.profiles.indexOf(profile)
    if (index >= 0) {
      this.profiles.splice(index, 1)
      this.reactivate()
    }
    if (identity_id) await api.deleteIdentity(identity_id)
  }

  cleanupUnsavedProfiles() {
    this.profiles = this.profiles.filter(it => !!it.identity_id || it === this.profile)
    if (!this.profiles.includes(this.profile)) {
      this.reactivate()
    }
  }

  async deleteAll() {
    const profiles = this.profiles
    this.profiles = []
    await Promise.all(profiles.map(profile => this.deleteProfile(profile)))
  }

  private reactivate() {
    const id = this.active_identity_id
    if (id) {
      const selected = this.profiles.find(it => it.identity_id === id)
      if (selected) {
        this.profile = selected
        this.cleanupUnsavedProfiles()
        return
      }
    }

    const saved = this.profiles.find(it => !!it.identity_id)
    if (saved) {
      this.profile = saved
      this.cleanupUnsavedProfiles()
      return
    }

    let empty = this.profiles[0]
    if (!empty) {
      empty = new ProfileStore(this._config, undefined)
      this.profiles.push(empty)
    }
    this.profile = empty
  }
}
